import { useRef, useState } from 'react';

import { Box, CircularProgress, IconButton, TextField } from '@mui/material';
import Check from '@mui/icons-material/Check';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';

import { BaseEditControlProps, DateDataTypes } from '../Table.types';

const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm';

export const DateEditControl = ({
  initialValue,
  onSubmitChange,
  type = 'datetime',
  isSaving = false,
  isInvalid,
  ...controlProps
}: BaseEditControlProps<DateDataTypes>) => {
  const [value, setValue] = useState(() => (initialValue ? moment(initialValue) : null));
  const [textValue, setTextValue] = useState(() =>
    initialValue && initialValue !== 'N/A' ? moment(initialValue).format(DATETIME_FORMAT) : '',
  );
  const [isValid, setIsValid] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);

  const isDatetime = type === 'datetime';
  const parsedDatetime = moment(textValue, DATETIME_FORMAT, true);
  const submitValue = isDatetime ? parsedDatetime : value;
  const hasChanged = isDatetime
    ? parsedDatetime.isValid() && !parsedDatetime.isSame(moment(initialValue))
    : !moment(value).isSame(moment(initialValue));
  const canSubmit = isDatetime ? parsedDatetime.isValid() : isValid;

  const handleOnError = (error: any) => {
    setIsValid(!error);
  };

  const handleOnKeyDown = event => {
    if (event.key === 'Enter' && canSubmit && hasChanged && !isSaving) {
      onSubmitChange(submitValue);
    }
    controlProps?.onKeyDown?.(event);
  };

  const getDateControlByType = (type: string): JSX.Element => {
    if (type === 'datetime') {
      return (
        <TextField
          {...controlProps}
          inputRef={inputRef}
          fullWidth
          size="small"
          error={isInvalid || (textValue !== '' && !parsedDatetime.isValid())}
          value={textValue}
          autoFocus
          onChange={event => setTextValue(event.target.value)}
          onFocus={event => {
            requestAnimationFrame(() => {
              const end = inputRef.current?.value.length ?? 0;
              inputRef.current?.setSelectionRange(end, end);
            });
            controlProps?.onFocus?.(event);
          }}
          onKeyDown={handleOnKeyDown}
        />
      );
    }

    const commonPickerProps: any = {
      value,
      onChange: (newValue: any) => setValue(newValue),
      onError: handleOnError,
      slotProps: {
        toolbar: {
          backgroundColor: '#1e1e1e',
        },
        layout: {
          sx: {
            display: 'flex',
            flexDirection: 'column',
          },
        },
        actionBar: {
          sx: {
            justifyContent: 'center',
          },
        },
        textField: {
          fullWidth: true,
          size: 'small',
          error: isInvalid,
          onKeyDown: handleOnKeyDown,
          ...controlProps,
        },
      },
    };

    return <DatePicker {...commonPickerProps} format="YYYY-MM-DD" />;
  };

  return (
    <Box display="flex" flexGrow={1} width="100%" alignItems="center">
      {getDateControlByType(type)}
      <IconButton
        size="small"
        disableFocusRipple={true}
        disableRipple
        disabled={!canSubmit || isSaving || !hasChanged}
        onClick={() => onSubmitChange(submitValue)}
      >
        {isSaving ? (
          <CircularProgress color="primary" variant="indeterminate" size={16} />
        ) : (
          <Check fontSize="small" />
        )}
      </IconButton>
    </Box>
  );
};
